import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import axios from "../../../../api/axios";
import { 
  ChartBarIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  TruckIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  WrenchScrewdriverIcon,
  ListBulletIcon
} from "@heroicons/react/24/outline";

// Cost Breakdown Chart Component
const CostBreakdownChart = ({ laborCosts, materialCosts, supplierInvoices, totalCosts, formatCurrency, t }) => {
  const size = 200;
  const center = size / 2;
  const radius = 80;
  const strokeWidth = 40;

  // Calculate percentages
  const laborPercent = totalCosts > 0 ? (laborCosts / totalCosts) * 100 : 0;
  const materialPercent = totalCosts > 0 ? (materialCosts / totalCosts) * 100 : 0;
  const supplierPercent = totalCosts > 0 ? (supplierInvoices / totalCosts) * 100 : 0;

  // Calculate angles for pie chart
  const circumference = 2 * Math.PI * radius;
  
  // Calculate stroke dash offsets (start from top, going clockwise)
  const laborLength = (laborPercent / 100) * circumference;
  const materialLength = (materialPercent / 100) * circumference;
  const supplierLength = (supplierPercent / 100) * circumference;
  
  // Offsets: each segment starts where the previous one ended
  const laborOffset = circumference - laborLength;
  const materialOffset = circumference - materialLength - laborLength;
  const supplierOffset = circumference - supplierLength - materialLength - laborLength;

  const colors = {
    labor: '#3B82F6', // blue
    material: '#10B981', // green
    supplier: '#F59E0B' // amber
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Pie Chart */}
      <div className="flex items-center justify-center">
        <svg width={size} height={size} className="transform -rotate-90">
          {/* Labor costs - first segment */}
          {laborPercent > 0 && (
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={colors.labor}
              strokeWidth={strokeWidth}
              strokeDasharray={`${laborLength} ${circumference}`}
              strokeDashoffset={laborOffset}
              strokeLinecap="round"
            />
          )}
          {/* Material costs - second segment */}
          {materialPercent > 0 && (
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={colors.material}
              strokeWidth={strokeWidth}
              strokeDasharray={`${materialLength} ${circumference}`}
              strokeDashoffset={materialOffset}
              strokeLinecap="round"
            />
          )}
          {/* Supplier invoices - third segment */}
          {supplierPercent > 0 && (
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={colors.supplier}
              strokeWidth={strokeWidth}
              strokeDasharray={`${supplierLength} ${circumference}`}
              strokeDashoffset={supplierOffset}
              strokeLinecap="round"
            />
          )}
        </svg>
      </div>

      {/* Legend and Details */}
      <div className="space-y-4">
        <div className="space-y-3">
          {/* Labor Costs */}
          {laborPercent > 0 && (
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-4 h-4 rounded-full mr-3" style={{ backgroundColor: colors.labor }}></div>
                <span className="text-sm font-medium text-gray-900">
                  {t("project.summary.labor_costs") || "Arbeidskostnader"}
                </span>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-900">
                  {formatCurrency(laborCosts)}
                </div>
                <div className="text-xs text-gray-500">
                  {laborPercent.toFixed(1)}%
                </div>
              </div>
            </div>
          )}

          {/* Material Costs */}
          {materialPercent > 0 && (
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-4 h-4 rounded-full mr-3" style={{ backgroundColor: colors.material }}></div>
                <span className="text-sm font-medium text-gray-900">
                  {t("project.summary.material_costs") || "Materialkostnader"}
                </span>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-900">
                  {formatCurrency(materialCosts)}
                </div>
                <div className="text-xs text-gray-500">
                  {materialPercent.toFixed(1)}%
                </div>
              </div>
            </div>
          )}

          {/* Supplier Invoices */}
          {supplierPercent > 0 && (
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-4 h-4 rounded-full mr-3" style={{ backgroundColor: colors.supplier }}></div>
                <span className="text-sm font-medium text-gray-900">
                  {t("project.summary.supplier_invoices") || "Leverandørfakturaer"}
                </span>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-900">
                  {formatCurrency(supplierInvoices)}
                </div>
                <div className="text-xs text-gray-500">
                  {supplierPercent.toFixed(1)}%
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Total */}
        <div className="border-t border-gray-200 pt-3 mt-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-900">
              {t("project.summary.total_costs") || "Totale kostnader"}
            </span>
            <span className="text-lg font-bold text-gray-900">
              {formatCurrency(totalCosts)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ProjectSummaryTab = ({ project }) => {
  const { t } = useTranslation();
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoading(true);
        setSummaryData(null);
        const response = await axios.get(
          `/projects/${project.id}/summary?include_modules=true`
        );
        if (response.data.success) {
          setSummaryData(response.data.data);
        } else {
          setError("Failed to load project summary");
        }
      } catch (err) {
        console.error("Error fetching project summary:", err);
        setError("Failed to load project summary");
      } finally {
        setLoading(false);
      }
    };

    if (project?.id) {
      fetchSummary();
    }
  }, [project?.id]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('no-NO', {
      style: 'currency',
      currency: 'NOK'
    }).format(amount);
  };

  const formatPercentage = (value) => {
    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    return `${numValue.toFixed(1)}%`;
  };

  const getTrendIcon = (value) => {
    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    if (numValue > 0) return <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />;
    if (numValue < 0) return <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />;
    return <MinusIcon className="h-4 w-4 text-gray-500" />;
  };

  const getTrendColor = (value) => {
    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    if (numValue > 0) return "text-green-600";
    if (numValue < 0) return "text-red-600";
    return "text-gray-600";
  };

  const handleExportPdf = async () => {
    try {
      const response = await axios.get(`/projects/${project.id}/summary_pdf`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });
      
      // Check if response is actually a PDF
      if (response.data.type && response.data.type !== 'application/pdf') {
        // Try to parse as JSON to get error message
        const text = await response.data.text();
        try {
          const json = JSON.parse(text);
          throw new Error(json.errors?.[0] || json.message || 'Unknown error');
        } catch (e) {
          throw new Error('Response is not a PDF file');
        }
      }
      
      // Validate PDF content
      const arrayBuffer = await response.data.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const pdfHeader = String.fromCharCode(...uint8Array.slice(0, 4));
      
      if (pdfHeader !== '%PDF') {
        throw new Error('Invalid PDF file - missing PDF header');
      }
      
      // Create a blob URL and trigger download
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `prosjektsummering_${project.id}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting PDF:", err);
      const errorMessage = err.response?.data?.errors?.[0] || err.message || 'Kunne ikke eksportere PDF';
      alert(`Kunne ikke eksportere PDF: ${errorMessage}`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="h-12 w-12 mx-auto text-red-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t("project.summary.error.title") || "Feil ved lasting"}
        </h3>
        <p className="text-gray-500">{error}</p>
      </div>
    );
  }

  if (!summaryData) {
    return (
      <div className="text-center py-12">
        <ChartBarIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t("project.summary.no_data.title") || "Ingen data tilgjengelig"}
        </h3>
        <p className="text-gray-500">
          {t("project.summary.no_data.description") || "Prosjektsummering vil bli generert når data er tilgjengelig"}
        </p>
      </div>
    );
  }

  const contractSum = summaryData.contract_sum || 0;
  const invoicedAmount = summaryData.invoiced_amount || 0;
  const marginBasis = summaryData.margin_basis || "contract";
  const totalCostsVal = summaryData.total_costs || 0;
  const invoicingRatePercent =
    contractSum > 0 ? (invoicedAmount / contractSum) * 100 : null;
  const costRateDenominator = contractSum > 0 ? contractSum : invoicedAmount;
  const costRatePercent =
    costRateDenominator > 0 ? (totalCostsVal / costRateDenominator) * 100 : 0;
  const marginFootnoteAmount =
    marginBasis === "invoiced" ? invoicedAmount : contractSum;
  const marginFootnoteLabel =
    marginBasis === "invoiced"
      ? t("project.summary.invoiced_ex_vat_basis")
      : t("project.billing_plan.contract_sum");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          {t("project.summary.overview")}
        </h3>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            {t("project.summary.last_updated") || "Sist oppdatert"}: {new Date(summaryData.last_updated).toLocaleString('no-NO')}
          </div>
          <button
            onClick={handleExportPdf}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            {t("project.summary.export_pdf") || "Eksporter PDF"}
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t("project.billing_plan.contract_sum")}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(summaryData.contract_sum || 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t("project.summary.profit")}
                  </dt>
                  <dd className={`text-lg font-medium ${getTrendColor(summaryData.profit || 0)}`}>
                    {formatCurrency(summaryData.profit || 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ArrowTrendingUpIcon className="h-6 w-6 text-purple-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t("project.summary.margin")}
                  </dt>
                  <dd className={`text-lg font-medium ${getTrendColor(summaryData.margin || 0)}`}>
                    {formatPercentage(summaryData.margin || 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t("project.summary.provisional_result")}
                  </dt>
                  <dd className={`text-lg font-medium ${getTrendColor(summaryData.provisional_result || 0)}`}>
                    {formatCurrency(summaryData.provisional_result || 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-6 w-6 text-indigo-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t("project.billing_plan.remaining_amount") || "Innestående beløp"}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(summaryData.remaining_amount || 0)}
                  </dd>
                  {summaryData.expected_remaining_amount > 0 && contractSum > 0 && (
                    <dd className="text-xs text-gray-500 mt-1">
                      Forventet: {formatCurrency(summaryData.expected_remaining_amount || 0)} ({formatPercentage(summaryData.expected_remaining_percentage || 0)} av kontrakt)
                    </dd>
                  )}
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income */}
        <div className="bg-white shadow rounded-lg p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">
            Inntekter
          </h4>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">{t("project.summary.contract_sum") || "Kontraktsum"}</span>
              <span className="text-sm font-medium text-gray-900">
                {formatCurrency(summaryData.contract_sum || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">{t("project.summary.invoiced_amount") || "Fakturert beløp"}</span>
              <span className="text-sm font-medium text-gray-900">
                {formatCurrency(summaryData.invoiced_amount || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">{t("project.billing_plan.remaining_amount") || "Innestående beløp"} (faktisk)</span>
              <span className="text-sm font-medium text-gray-900">
                {formatCurrency(summaryData.remaining_amount || 0)}
              </span>
            </div>
            {summaryData.expected_remaining_amount > 0 && contractSum > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Forventet innestående ({formatPercentage(summaryData.expected_remaining_percentage || 0)} av kontrakt)
                </span>
                <span className="text-sm font-medium text-gray-900">
                  {formatCurrency(summaryData.expected_remaining_amount || 0)}
                </span>
              </div>
            )}
            {summaryData.expected_module_income > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{t("project.summary.expected_module_income") || "Forventet inntekt fra moduler"}</span>
                <span className="text-sm font-medium text-green-600">
                  {formatCurrency(summaryData.expected_module_income || 0)}
                </span>
              </div>
            )}
            {contractSum > 0 && (
            <div className="border-t border-gray-200 pt-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-900">{t("project.summary.remaining") || "Gjenstående"}</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatCurrency(contractSum - invoicedAmount)}
                </span>
              </div>
            </div>
            )}
          </div>
        </div>

        {/* Costs */}
        <div className="bg-white shadow rounded-lg p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">
            Kostnader
          </h4>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <CurrencyDollarIcon className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm text-gray-600">{t("project.summary.labor_costs")}</span>
              </div>
              <span className="text-sm font-medium text-gray-900">
                {formatCurrency(summaryData.labor_costs || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <ClipboardDocumentListIcon className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm text-gray-600">{t("project.summary.material_costs")}</span>
              </div>
              <span className="text-sm font-medium text-gray-900">
                {formatCurrency(summaryData.material_costs || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <TruckIcon className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm text-gray-600">{t("project.summary.supplier_invoices")}</span>
              </div>
              <span className="text-sm font-medium text-gray-900">
                {formatCurrency(summaryData.supplier_invoices || 0)}
              </span>
            </div>
            {summaryData.expected_module_costs > 0 && (
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <DocumentTextIcon className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">{t("project.summary.expected_module_costs") || "Forventet modulkostnader"}</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {formatCurrency(summaryData.expected_module_costs || 0)}
                </span>
              </div>
            )}
            <div className="border-t border-gray-200 pt-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-900">{t("project.summary.total_costs")}</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatCurrency(summaryData.total_costs || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profitability Analysis */}
      <div className="bg-white shadow rounded-lg p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">
          Lønnsomhetsanalyse
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 mb-2">
              {formatPercentage(summaryData.margin || 0)}
            </div>
            <div className="text-sm text-gray-600">
              {t("project.summary.margin")}
              {marginBasis === "invoiced" ? (
                <span className="block text-xs text-gray-500 mt-1">
                  {t("project.summary.margin_on_invoiced_hint")}
                </span>
              ) : null}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {formatCurrency(summaryData.profit || 0)} {t("project.summary.of")}{" "}
              {formatCurrency(marginFootnoteAmount || 0)}{" "}
              <span className="text-gray-400">({marginFootnoteLabel})</span>
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 mb-2">
              {invoicingRatePercent != null
                ? formatPercentage(invoicingRatePercent)
                : "—"}
            </div>
            <div className="text-sm text-gray-600">{t("project.summary.invoicing_rate") || "Faktureringsgrad"}</div>
            <div className="text-xs text-gray-500 mt-1">
              {formatCurrency(invoicedAmount)}{" "}
              {invoicingRatePercent != null ? (
                <>
                  {t("project.summary.of")} {formatCurrency(contractSum)}
                </>
              ) : (
                <span className="text-gray-400">({t("project.summary.invoicing_rate_no_contract")})</span>
              )}
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600 mb-2">
              {formatPercentage(costRatePercent)}
            </div>
            <div className="text-sm text-gray-600">{t("project.summary.cost_rate") || "Kostnadsgrad"}</div>
            <div className="text-xs text-gray-500 mt-1">
              {formatCurrency(totalCostsVal)}{" "}
              {t("project.summary.of")}{" "}
              {formatCurrency(costRateDenominator || 0)}
              {contractSum <= 0 && invoicedAmount > 0 ? (
                <span className="text-gray-400"> ({t("project.summary.cost_rate_on_invoiced_hint")})</span>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Cost Breakdown Chart */}
      <div className="bg-white shadow rounded-lg p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">
          {t("project.summary.cost_breakdown") || "Kostnadsfordeling"}
        </h4>
        {summaryData.total_costs > 0 ? (
          <CostBreakdownChart 
            laborCosts={summaryData.labor_costs || 0}
            materialCosts={summaryData.material_costs || 0}
            supplierInvoices={summaryData.supplier_invoices || 0}
            totalCosts={summaryData.total_costs || 0}
            formatCurrency={formatCurrency}
            t={t}
          />
        ) : (
          <div className="text-center py-8 text-gray-500">
            <ChartBarIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>{t("project.summary.no_costs") || "Ingen kostnader registrert ennå"}</p>
          </div>
        )}
      </div>

      {/* Module Breakdown */}
      {summaryData.modules && summaryData.modules.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">
            {t("project.summary.module_breakdown") || "Moduloversikt"}
          </h4>
          <p className="text-sm text-gray-500 mb-4">
            {t("project.summary.module_breakdown_description") || "Oversikt over alle moduler som påvirker prosjektsummeringen"}
          </p>
          <div className="space-y-4">
            {summaryData.modules.map((module, index) => {
              const getModuleIcon = () => {
                switch (module.module_type) {
                  case 'report':
                    return <DocumentTextIcon className="h-5 w-5 text-blue-500" />;
                  case 'work_order':
                    return <WrenchScrewdriverIcon className="h-5 w-5 text-purple-500" />;
                  case 'item_list':
                    return <ListBulletIcon className="h-5 w-5 text-green-500" />;
                  default:
                    return <ClipboardDocumentListIcon className="h-5 w-5 text-gray-500" />;
                }
              };

              const getModuleTypeLabel = () => {
                switch (module.module_type) {
                  case 'report':
                    return t("project.summary.module_type.report") || "Rapport";
                  case 'work_order':
                    return t("project.summary.module_type.work_order") || "Arbeidsordre";
                  case 'item_list':
                    return t("project.summary.module_type.item_list") || "Vareliste";
                  default:
                    return module.module_type;
                }
              };

              return (
                <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getModuleIcon()}
                        <span className="text-xs font-semibold text-gray-500 uppercase">
                          {getModuleTypeLabel()}
                        </span>
                        {module.invoiced && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            {t("project.summary.invoiced") || "Fakturert"}
                          </span>
                        )}
                        {(module.has_order || module.has_orders) && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            {t("project.summary.has_order") || "Har ordre"}
                          </span>
                        )}
                      </div>
                      <Link
                        to={module.link}
                        className="text-base font-semibold text-gray-900 hover:text-blue-600 transition-colors"
                      >
                        {module.display_title}
                      </Link>
                      
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Costs */}
                        <div className="bg-red-50 rounded-lg p-3">
                          <div className="text-xs font-semibold text-gray-700 uppercase mb-1">
                            {t("project.summary.costs") || "Kostnader"}
                          </div>
                          <div className="text-sm font-bold text-red-600">
                            {formatCurrency(module.costs.total || 0)}
                          </div>
                          {module.costs.hours > 0 && (
                            <div className="text-xs text-gray-600 mt-1">
                              {t("project.summary.hours") || "Timer"}: {formatCurrency(module.costs.hours)}
                            </div>
                          )}
                          {module.material_costs > 0 && (
                            <div className="text-xs text-gray-600 mt-1">
                              {t("project.summary.material") || "Materialer"}: {formatCurrency(module.material_costs)}
                            </div>
                          )}
                        </div>

                        {/* Income */}
                        <div className="bg-green-50 rounded-lg p-3">
                          <div className="text-xs font-semibold text-gray-700 uppercase mb-1">
                            {t("project.summary.income") || "Inntekter"}
                          </div>
                          <div className="text-sm font-bold text-green-600">
                            {formatCurrency(module.income.total || 0)}
                          </div>
                          {module.income.hours > 0 && (
                            <div className="text-xs text-gray-600 mt-1">
                              {t("project.summary.hours") || "Timer"}: {formatCurrency(module.income.hours)}
                            </div>
                          )}
                          {module.income.agreed_price > 0 && (
                            <div className="text-xs text-gray-600 mt-1">
                              {t("project.summary.agreed_price") || "Avtalt pris"}: {formatCurrency(module.income.agreed_price)}
                            </div>
                          )}
                        </div>

                        {/* Profit */}
                        <div className={`rounded-lg p-3 ${module.profit >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                          <div className="text-xs font-semibold text-gray-700 uppercase mb-1">
                            {t("project.summary.profit") || "Fortjeneste"}
                          </div>
                          <div className={`text-sm font-bold ${module.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(module.profit || 0)}
                          </div>
                          {module.hours && module.hours.registered > 0 && (
                            <div className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                              <ClockIcon className="h-3 w-3" />
                              {(parseFloat(module.hours.registered) || 0).toFixed(1)} {t("project.summary.hours_short") || "t"}
                            </div>
                          )}
                          {module.price_lines && module.price_lines.length > 0 && (
                            <div className="text-xs text-gray-600 mt-1">
                              {module.price_lines.length} {t("project.summary.price_lines") || "prislinjer"}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
